import React from "react";
import { Icon, EmptyState, notify, confirmDialog } from "./registry";
/* ============================================================
   Fence Works — FW Admin → Phone lines (platform owner only)
   Every tenant's business lines in one table (migration #48; docs/VOIP_PLAN.md
   §8): the number, its brand, where it came from, its status and texting
   registration, the week's calls, and the actions we take on a customer's
   behalf — adopt their registered texting number as their line, buy one,
   release one. The AI receptionist numbers are listed beside them so the
   whole inventory is on one page.
   ============================================================ */
const opsApi = (): any => (window as any).VFP_DB && (window as any).VFP_DB.ops;
const pretty = (e: string) => { const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(String(e || "")); return m ? `(${m[1]}) ${m[2]}-${m[3]}` : String(e || ""); };

function PhoneLinesAdminPage({ go }: any) {
  const api = opsApi();
  const live = !!(api && api.enabled && api.enabled() && api.lines);
  const [data, setData] = React.useState<any>(null);
  const [err, setErr] = React.useState("");
  const [denied, setDenied] = React.useState(false);
  const [busy, setBusy] = React.useState("");
  const [buyWs, setBuyWs] = React.useState("");
  const [buyArea, setBuyArea] = React.useState("920");

  const load = React.useCallback(() => {
    if (!live) return;
    setErr("");
    api.lines().then((d: any) => { setData(d); setDenied(false); })
      .catch((e: any) => { if (/403|forbidden|owner/i.test(String(e.message || e))) setDenied(true); else setErr(String(e.message || e)); });
  }, [live]);
  React.useEffect(() => { load(); }, [load]);

  async function act(key: string, fn: () => Promise<any>, done: string) {
    setBusy(key);
    try { await fn(); notify(done); }
    catch (e: any) { notify(String(e.message || e), { kind: "error", ms: 12000 }); }
    finally { setBusy(""); load(); }   // reload either way: a failed buy leaves an error row worth seeing
  }

  if (!live) return <div className="page fade-up"><h1 className="display" style={{ fontSize: 26 }}>Phone lines</h1><EmptyState icon="phone" title="Needs the live service" body="Every tenant's lines are read from the service." /></div>;
  if (denied) return <div className="page fade-up"><h1 className="display" style={{ fontSize: 26 }}>Phone lines</h1><p className="muted">Platform owner only.</p></div>;
  if (err) return <div className="page fade-up"><h1 className="display" style={{ fontSize: 26 }}>Phone lines</h1><p className="muted" style={{ color: "var(--red)" }}>{err}</p></div>;
  if (!data) return <div className="page fade-up"><h1 className="display" style={{ fontSize: 26 }}>Phone lines</h1><p className="muted">Loading…</p></div>;

  const lines: any[] = data.lines || [];
  // the customer to buy for: typed by company name (or id), resolved to the workspace
  const wsList: any[] = data.workspaces || [];
  const q = buyWs.trim().toLowerCase();
  const exact = q ? (wsList.find((w) => String(w.id).toLowerCase() === q) || wsList.find((w) => String(w.company || "").toLowerCase() === q)) : null;
  const partial = q ? wsList.filter((w) => String(w.company || "").toLowerCase().includes(q) || String(w.id).toLowerCase().includes(q)) : [];
  const buyTarget = exact || (partial.length === 1 ? partial[0] : null);
  return (
    <div className="page fade-up" style={{ maxWidth: 1100 }}>
      <h1 className="display" style={{ fontSize: 26, margin: "0 0 6px" }}>Phone lines <span className="chip" style={{ marginLeft: 8 }}>FW Admin</span></h1>
      <p className="muted" style={{ fontSize: 14, margin: "0 0 14px" }}>Every tenant's business line, what it did this week, and the registered texting numbers that could become one.</p>
      {!data.setup && <div className="pe-callout warn"><p className="muted" style={{ margin: 0 }}>{`Run migration #48 (${data.migration}).`}</p></div>}
      {data.setup && !data.configured && <div className="pe-callout warn"><p className="muted" style={{ margin: 0 }}>Twilio credentials or a public SERVICE_URL are missing on this server; nothing can be bought or adopted.</p></div>}

      <div className="card pad">
        <h2 className="display" style={{ fontSize: 18, margin: "0 0 8px" }}>Business lines ({lines.length})</h2>
        {!lines.length && <p className="muted" style={{ fontSize: 13 }}>None yet.</p>}
        {lines.length > 0 && (
          <table className="table" style={{ width: "100%", fontSize: 13 }}>
            <thead><tr><th>Customer</th><th>Brand</th><th>Number</th><th>Source</th><th>Status</th><th>Texting</th><th>Calls 7d</th><th>Missed</th><th>AI</th><th>Unheard VM</th><th></th></tr></thead>
            <tbody>
              {lines.map((l: any) => (
                <tr key={l.id}>
                  <td><a href="#" onClick={(e) => { e.preventDefault(); go("fwcustomers", { ws: l.workspace_id }); }}>{l.company}</a></td>
                  <td>{l.vertical || "company"}</td>
                  <td><b>{pretty(l.e164) || "—"}</b></td>
                  <td>{l.source}</td>
                  <td><span className={"chip " + (l.status === "active" ? "teal" : l.status === "error" ? "red" : "")}>{l.status}</span>{l.error && <div className="faint" style={{ fontSize: 11.5, marginTop: 3, maxWidth: 260, whiteSpace: "normal" }} title={l.error}>{String(l.error).replace(/^Twilio \w+ \S+ → \d+: /, "").slice(0, 160)}</div>}</td>
                  <td>{l.registration}</td>
                  <td>{l.calls7d}</td><td>{l.missed7d}</td><td>{l.ai7d}</td><td>{l.unheard}</td>
                  <td>{l.status === "error"
                    ? <button className="btn btn-sm" disabled={!!busy} title="Nothing was rented; this clears the failed attempt. Buying again for this customer also reuses it." onClick={() => act("rel:" + l.id, () => api.lineRelease(l.id), "Cleared.")}>Clear</button>
                    : <button className="btn" disabled={!!busy} onClick={async () => {
                    const ok = await confirmDialog({ title: `Release ${pretty(l.e164)} for ${l.company}?`, body: l.source === "adopted" ? "Calls stop being handled; the number stays their registered texting number." : "The number is released at Twilio and can't be recovered.", confirmLabel: "Release" });
                    if (ok) act("rel:" + l.id, () => api.lineRelease(l.id), "Released.");
                  }}><Icon name="trash" size={13} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(data.adoptable || []).length > 0 && (
        <div className="card pad" style={{ marginTop: 14 }}>
          <h2 className="display" style={{ fontSize: 18, margin: "0 0 8px" }}>Registered texting numbers without a line</h2>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 8px" }}>Adopting makes the texting number the business line: same number, now with calls. Routing starts as "ring the owner's cell, then the AI receptionist or voicemail"; the customer refines it on their Business line screen.</p>
          {(data.adoptable || []).map((a: any) => (
            <div key={a.workspace_id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", borderTop: "1px solid var(--line)", fontSize: 13 }}>
              <b style={{ minWidth: 180 }}>{a.company}</b><span>{pretty(a.number)}</span><span className="faint">texting {a.status}</span>
              <button className="btn" style={{ marginLeft: "auto" }} disabled={!!busy || !data.configured} onClick={() => act("adopt:" + a.workspace_id, () => api.lineAdopt(a.workspace_id, {}), `${pretty(a.number)} is now ${a.company}'s business line.`)}><Icon name="phone" size={13} />Adopt as their line</button>
            </div>
          ))}
        </div>
      )}

      <div className="card pad" style={{ marginTop: 14 }}>
        <h2 className="display" style={{ fontSize: 18, margin: "0 0 8px" }}>Buy a line for a customer</h2>
        {(data.adoptable || []).length === 0 && <p className="muted" style={{ fontSize: 12.5, margin: "0 0 10px" }}>A customer whose texting is registered (Messages → Texts in their app) shows an <b>Adopt</b> button above instead: their registered number becomes the line and keeps its campaign. None is registered yet, so a new number is the way.</p>}
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="ai-field" style={{ margin: 0 }}>
            <label className="field-label">Customer</label>
            <input className="input" style={{ width: 320 }} list="fw-lines-ws" placeholder="Start typing the company name" value={buyWs} onChange={(e) => setBuyWs(e.target.value)} />
            <datalist id="fw-lines-ws">{wsList.map((w) => <option key={w.id} value={w.company}>{w.id}{w.phone ? " · " + pretty(w.phone) : ""}</option>)}</datalist>
            <div className="faint" style={{ fontSize: 11.5, marginTop: 4, minHeight: 14 }}>
              {buyTarget ? <>→ <b>{buyTarget.company}</b> <span className="mono">({buyTarget.id})</span></> : q ? (partial.length ? `${partial.length} matches — keep typing` : "No customer by that name") : `${wsList.length} customers`}
            </div>
          </div>
          <div className="ai-field" style={{ margin: 0 }}><label className="field-label">Area code</label><input className="input" style={{ width: 100 }} value={buyArea} onChange={(e) => setBuyArea(e.target.value.replace(/\D/g, "").slice(0, 3))} /></div>
          <button className="btn btn-primary" disabled={!!busy || !buyTarget || !data.configured} onClick={() => act("buy", () => api.lineBuy(buyTarget.id, { areaCode: buyArea }), `Bought a line for ${buyTarget.company}.`)}><Icon name="plus" size={14} />Buy</button>
        </div>
      </div>

      {(data.aiNumbers || []).length > 0 && (
        <div className="card pad" style={{ marginTop: 14 }}>
          <h2 className="display" style={{ fontSize: 18, margin: "0 0 8px" }}>AI receptionist numbers</h2>
          <table className="table" style={{ width: "100%", fontSize: 13 }}>
            <thead><tr><th>Customer</th><th>Service</th><th>Number</th></tr></thead>
            <tbody>{(data.aiNumbers || []).map((a: any, i: number) => <tr key={i}><td>{a.company}</td><td>{a.vertical}</td><td>{pretty(a.number)}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export { PhoneLinesAdminPage };
